import React from 'react'
import PropTypes from 'prop-types'
import { NavLink, useLocation } from 'react-router-dom'
import { Menu, Icon, Label, Loader } from 'semantic-ui-react'
import { LIST_TYPES } from '../../utils/marketplaceConstants.js'
import {
  SidebarContainer,
  CreateOrderSection,
  CreateOrderLink,
  OverviewSection,
  FilterList,
  FilterItem,
  FilterItemContent,
  FilterDivider,
  OverviewLink,
  StreetTurnsLink,
  StreetTurnsContent,
  LoadingIndicator,
  FiltersSection,
} from './MarketplaceSidebar.styled.js'

/**
 * MarketplaceSidebarView
 *
 * Pure presentation component for the marketplace sidebar.
 * Displays category filters with counts and navigation.
 */
export const MarketplaceSidebarView = ({
  categories,
  listType,
  marketsCsv,
  search,
  isLoading,
  createOrderUrl,
  streetTurnsUrl,
  streetTurnCount,
  showOverview,
  showOrderOverviewLink,
  showStreetTurns,
  showContainerDashboard,
  showLanes,
  showCreateOrder,
  showFilters,
  filterCount,
  onCategorySelect,
  renderFilters,
}) => {
  const location = useLocation()
  const searchQuery = search ? `?query=${search}` : ''

  if (isLoading) {
    return (
      <Menu text vertical className='app-side-nav'>
        <LoadingIndicator>
          <Loader active inline size='small' />
        </LoadingIndicator>
      </Menu>
    )
  }

  return (
    <Menu text vertical className='app-side-nav'>
      <SidebarContainer>
        {/* Create Order Button - Orders mode only */}
        {showCreateOrder && listType === LIST_TYPES.ORDERS && (
          <CreateOrderSection>
            <CreateOrderLink
              as={NavLink}
              to={createOrderUrl}
              state={{ from: location.pathname + location.search }}
              data-testid='sidebar-create-order-link'
            >
              <Icon name='add square' />
              Create New Order
            </CreateOrderLink>
            <hr />
          </CreateOrderSection>
        )}

        {/* Admin Filters - positioned after Create Order like original */}
        {showFilters && renderFilters && (
          <FiltersSection>{renderFilters()}</FiltersSection>
        )}

        {/* Overview Section - Admin/IMC only */}
        {showOverview && listType === LIST_TYPES.ORDERS && (
          <OverviewSection>
            {showOrderOverviewLink && (
              <OverviewLink
                as={NavLink}
                to={`/marketplace/${marketsCsv}/overview${searchQuery}`}
                data-testid='sidebar-overview-link'
              >
                <Icon name='list' />
                Order Overview
              </OverviewLink>
            )}

            {showStreetTurns && (
              <StreetTurnsLink
                as={NavLink}
                to={streetTurnsUrl}
                className={
                  location.pathname.includes('/streetturns') ? 'active' : ''
                }
                data-testid='sidebar-street-turns-link'
              >
                <StreetTurnsContent>
                  <Icon name='linkify' />
                  Manage Street Turns
                </StreetTurnsContent>
                {streetTurnCount !== null && (
                  <Label content={streetTurnCount} />
                )}
              </StreetTurnsLink>
            )}

            {showContainerDashboard && (
              <OverviewLink
                as={NavLink}
                to={`/containers?market=${marketsCsv}`}
                data-testid='sidebar-containers-link'
              >
                <Icon name='boxes' />
                Container Dashboard
                <Label className='beta-label' size='mini'>
                  BETA
                </Label>
              </OverviewLink>
            )}
            <hr />
          </OverviewSection>
        )}

        {/* Lanes Link */}
        {showLanes && (
          <OverviewSection>
            <OverviewLink
              as={NavLink}
              to='/lanes'
              data-testid='sidebar-lanes-link'
            >
              <Icon name='star' />
              Lanes
            </OverviewLink>
            <hr />
          </OverviewSection>
        )}

        {/* Category Filters */}
        <FilterList>
          {categories.map((cat, index) => (
            <React.Fragment key={cat.key}>
              <FilterItem
                as={NavLink}
                to={`/marketplace/${marketsCsv}/${cat.key}${searchQuery}`}
                className={`${cat.isActive ? 'active' : ''} ${
                  cat.key === 'alerts' && cat.count > 0 ? 'active-alert' : ''
                }`}
                onClick={(e) => {
                  if (onCategorySelect) {
                    e.preventDefault()
                    onCategorySelect(cat.key)
                  }
                }}
              >
                <FilterItemContent>
                  <Icon name={cat.icon || 'circle'} />
                  {cat.title}
                </FilterItemContent>
                <Label content={cat.count} />
              </FilterItem>

              {/* Divider after alerts */}
              {cat.key === 'alerts' && <FilterDivider />}
            </React.Fragment>
          ))}
        </FilterList>
      </SidebarContainer>
    </Menu>
  )
}

MarketplaceSidebarView.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      icon: PropTypes.string,
      count: PropTypes.number,
      isActive: PropTypes.bool,
    }),
  ).isRequired,
  listType: PropTypes.oneOf([LIST_TYPES.ORDERS, LIST_TYPES.TRIPS]).isRequired,
  marketsCsv: PropTypes.string.isRequired,
  search: PropTypes.string,
  isLoading: PropTypes.bool,
  createOrderUrl: PropTypes.string,
  streetTurnsUrl: PropTypes.string,
  streetTurnCount: PropTypes.number,
  showOverview: PropTypes.bool,
  showOrderOverviewLink: PropTypes.bool,
  showStreetTurns: PropTypes.bool,
  showContainerDashboard: PropTypes.bool,
  showLanes: PropTypes.bool,
  showCreateOrder: PropTypes.bool,
  showFilters: PropTypes.bool,
  filterCount: PropTypes.number,
  onCategorySelect: PropTypes.func,
  renderFilters: PropTypes.func,
}

MarketplaceSidebarView.defaultProps = {
  search: '',
  isLoading: false,
  createOrderUrl: '/order/new',
  streetTurnsUrl: '/streetturns',
  streetTurnCount: null,
  showOverview: false,
  showOrderOverviewLink: false,
  showStreetTurns: false,
  showContainerDashboard: false,
  showLanes: false,
  showCreateOrder: true,
  showFilters: false,
  filterCount: 0,
  onCategorySelect: null,
  renderFilters: null,
}
